import { useState, useCallback } from 'react';
import { Button, Typography, Box, Paper, Grid } from '@mui/material';
import FlexCenter from '../../components/flex/flex-center';
import { ArrowBigRight, ArrowBigLeft } from 'lucide-react';

type WheelItem = {
    color: string;
    text: string;
};

export default function SlotMachineWheel() {
    const items: WheelItem[] = [
        { color: '#FF6633', text: 'Whenever Jacob drinks then Steve also needs to drink' },
        { color: '#FFB399', text: 'You are not allowed to point' },
        { color: '#FF33FF', text: 'You are not allowed to answer any questions from Jacob' },
        { color: '#FFFF99', text: 'You need to drink with your left hand' },
        { color: '#00B3E6', text: 'Whenever Steve takes a photo, everyone must try to be in it' },
        { color: '#3366E6', text: 'All current rules have been removed' },
        { color: '#99FF99', text: 'Some other crazy rule' },
        { color: '#B34D4D', text: 'You all must do stuff that you do not really want to do' }
    ];

    const [spinning, setSpinning] = useState(false);
    const [position, setPosition] = useState(0);
    const [, setSelectedIndex] = useState<number | null>(null);
    const [, setSelectedItem] = useState<WheelItem | null>(null);
    const [announcement, setAnnouncement] = useState('Spin the wheel!');

    const itemHeight = 80;
    const visibleItems = 3;
    const totalHeight = items.length * itemHeight;
    const animationSpeed = 4000; // ms
    const rotations = 7;

    const easeOutQuad = useCallback((t: number) => {
        return t * (2 - t);
    }, []);

    const animateReel = useCallback((startPosition: number, endPosition: number, onComplete: (index: number) => void) => {
        const startTime = Date.now();
        const positionDelta = endPosition - startPosition;

        const step = () => {
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;

            if (elapsed < animationSpeed) {
                const progress = elapsed / animationSpeed;
                const easeProgress = easeOutQuad(progress);
                setPosition(startPosition + positionDelta * easeProgress);
                requestAnimationFrame(step);
            } else {
                setPosition(endPosition);
                const finalReelTopOffset = endPosition % totalHeight;
                // Calculate the offset of the center line from the top of the reel
                const centerLineOffset = Math.floor(visibleItems / 2) * itemHeight;
                // Calculate the position of the item at the center line
                const centeredItemPosition = finalReelTopOffset + centerLineOffset;
                // Calculate the index of the item at the center line
                const finalLandedIndex = Math.floor(centeredItemPosition / itemHeight) % items.length;

                onComplete(finalLandedIndex);
                setSpinning(false);
            }
        };

        requestAnimationFrame(step);
    }, [animationSpeed, easeOutQuad, itemHeight, totalHeight, visibleItems]);

    const handleSpin = useCallback((targetIndex: number | null) => {
        if (spinning) return;

        setSpinning(true);
        setAnnouncement('Spinning...');
        setSelectedIndex(null);
        setSelectedItem(null);

        const fullRotations = rotations + Math.floor(Math.random() * 3);
        const centerItemIndex = Math.floor(visibleItems / 2); // Index of the visually centered item (0-based)
        const offsetToCenter = centerItemIndex * itemHeight;
        let endPosition;

        if (targetIndex !== null) {
            // First normalize the current position
            const normalizedPosition = position % totalHeight;

            // Calculate which item is currently at the center position
            const currentCenteredItemIndex = Math.floor((normalizedPosition + offsetToCenter) / itemHeight) % items.length;

            // Calculate how many items we need to move to get to the target index
            let stepsToTarget = (targetIndex - currentCenteredItemIndex) % items.length;
            if (stepsToTarget < 0) stepsToTarget += items.length;

            // Calculate the distance to move in pixels
            const distanceToMove = stepsToTarget * itemHeight;

            // Final end position is current position plus full rotations plus distance to target
            endPosition = position + (fullRotations * totalHeight) + distanceToMove;

            console.log("Forced Landing:");
            console.log("  targetIndex:", targetIndex);
            console.log("  currentCenteredItemIndex:", currentCenteredItemIndex);
            console.log("  stepsToTarget:", stepsToTarget);
            console.log("  distanceToMove:", distanceToMove);
            console.log("  endPosition:", endPosition);
        } else {
            const currentIndex = Math.floor((position % totalHeight) / itemHeight);
            const randomItemIndex = Math.floor(Math.random() * items.length);
            endPosition = position + (fullRotations * totalHeight) +
                ((randomItemIndex - currentIndex + items.length) % items.length) * itemHeight +
                offsetToCenter;
            console.log("Random Spin - endPosition:", endPosition);
        }

        animateReel(position, endPosition, (finalIndex: number) => {
            setSelectedIndex(finalIndex);
            setSelectedItem(items[finalIndex]);
            setAnnouncement(`${items[finalIndex].text}!`);
            console.log("Final Landed Index:", finalIndex);
        });
    }, [animateReel, items, position, spinning, itemHeight, totalHeight, visibleItems]);

    const spinRandomly = useCallback(() => {
        handleSpin(null);
    }, [handleSpin]);

    const spinToIndex3 = useCallback(() => {
        handleSpin(3);
    }, [handleSpin]);

    const renderReelItems = useCallback(() => {
        const repeats = Math.ceil(visibleItems * 2 / items.length) + 1;
        const expandedItems = Array(repeats).fill(items).flat();
        const startOffset = position % totalHeight;
        const visibleStartIndex = Math.floor(startOffset / itemHeight);

        return expandedItems.slice(visibleStartIndex, visibleStartIndex + visibleItems + 2).map((item, idx) => {
            const itemPosition = (visibleStartIndex + idx) * itemHeight - startOffset;

            return (
                <Box
                    key={`${item.text}-${visibleStartIndex + idx}`}
                    sx={{
                        position: 'absolute',
                        top: itemPosition,
                        left: 0,
                        width: '100%',
                        height: `${itemHeight}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: item.color,
                        border: '1px solid #333',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#fff',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                        }}
                    >
                        {item.text}
                    </Typography>
                </Box>
            );
        });
    }, [itemHeight, items, position, totalHeight, visibleItems]);

    return (
        <FlexCenter direction='column' sx={{ height: 'calc(100vh - 55px)' }}>
            <Grid container spacing={2} padding={2} justifyContent="center" style={{ width: '100%' }}>
                <Grid size={{ xs: 12, md: 8, lg: 6 }} >

                    <Paper elevation={3} sx={{ p: 2, mb: 3, width: '100%', textAlign: 'center', bgcolor: spinning ? '#f0f4ff' : '#fff' }}>
                        <Typography variant="h6" color="black">
                            Welcome to the Moes.Beer Slot Machine! These rules must be followed for the entire game.
                        </Typography>
                    </Paper>

                    <Box sx={{ position: 'relative', width: '100%', mb: 4 }}>
                        <Grid size={{ xs: 12 }} sx={{
                            position: 'relative',
                            height: `${visibleItems * itemHeight}px`,
                            overflow: 'hidden',
                            border: '4px solid #555',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.3), inset 0 0 10px rgba(0,0,0,0.2)',
                        }}>
                            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                {renderReelItems()}
                            </Box>
        
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%)',
                                pointerEvents: 'none'
                            }} />
                        </Grid>

                        <Box sx={{
                            position: 'absolute',
                            top: '45%',
                            left: '-14px',
                            right: '-14px',
                            height: '2px',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                            pointerEvents: 'none'
                        }}>
                            <span style={{ color: '#27adff', filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' }}><ArrowBigRight size={36} fill="#27adff" /></span>
                            <span style={{ color: '#27adff', filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' }}><ArrowBigLeft size={36} fill="#27adff" /></span>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={spinning}
                            onClick={spinRandomly}
                            sx={{ minWidth: '140px' }}
                        >
                            Spin Randomly
                        </Button>

                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={spinning}
                            onClick={spinToIndex3}
                            sx={{ minWidth: '140px' }}
                        >
                            Land on Index 3
                        </Button>
                    </Box>

                    <Paper elevation={2} sx={{ p: 2, width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/*<Box
                            sx={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                bgcolor: selectedItem?.color,
                                border: '1px solid #ccc'
                            }}
                        />*/}
                        <Typography>
                            {announcement}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </FlexCenter>
    );
}